import { getBorderRadius } from "@/utils/theme-helper";
import { SHADOW } from "@/utils/layered-frame";
import { EARLIER_LINE_HEIGHTS, LINE_HEIGHT_SCALE } from "@/utils/line-height";
import { lengthBox, lengthOrToken, spacingCss } from "./length";
import { preciseTypographyValue } from "./typography";
import {
  PURPOSE_STYLE_AREAS,
  imageWidthValue,
  leadShareValue,
  record,
  string,
  type PurposeSettings,
  type PurposeStyle,
} from "./model";

const widths: Record<string, string> = {
  full: "100%",
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
  "4xl": "1880px",
};
const sizes: Record<string, string> = {
  xs: ".75rem",
  sm: ".875rem",
  md: "1rem",
  lg: "1.25rem",
  xl: "1.5rem",
  "2xl": "2rem",
  "3xl": "2.5rem",
  "4xl": "3rem",
  "5xl": "4rem",
  "6xl": "5rem",
};
const tracking: Record<string, string> = {
  normal: "0",
  wide: "0.05em",
  wider: "0.12em",
};
const splitImage: Record<string, string> = {
  "1/4": "25%",
  "1/3": "33.333%",
  "2/5": "40%",
  "1/2": "50%",
  "3/5": "60%",
  "2/3": "66.667%",
};
/** An `imageWidth` value (a fraction from the cards or an own `"NN%"`) as the picture column's CSS width. */
export function imageShare(raw: unknown): string | undefined {
  const share = imageWidthValue(raw);
  return share && (splitImage[share] ?? share);
}
/** The content layer (ui.content) is the box around a section's content; `styles.content` keeps only its text styles. */
const CONTENT_BOX_STYLES = [
  "padding",
  "margin",
  "backgroundColor",
  "rounded",
  "width",
  "shadow",
  "borderWidth",
  "borderColor",
];
const COLUMN_COUNTS = ["1", "2", "3", "4", "5"];
/** Frostet glas (styles.copy.blur); only Formular med intro's Billede ved siden af draws it. */
const GLASS: Record<string, string> = { sm: "8px", md: "16px", lg: "24px" };
/** Areas whose padding also takes CSS lengths; an unreadable value draws the area's own default. */
const LENGTH_PADDING: Record<string, string> = {
  items: "24px",
  form: "24px",
  question: "18px 0px",
  answer: "0px 0px 20px",
};
/** Boxes whose padding takes CSS lengths and whose defaults depend on the family (Arrangement's date box and frame,
 *  Kontaktkort's card); they have no margin, and an unreadable padding sets nothing, so the family's own default is
 *  drawn. */
const FAMILY_BOXES = ["date", "card"];
const SHARE_COLUMNS: Record<string, [string, string]> = {
  "1/2": ["1fr", "1fr"],
  "3/5": ["3fr", "2fr"],
  "2/3": ["2fr", "1fr"],
};
/** A share from `leadShareValue` as the two columns' fractions, shared after the gap. */
function shareColumns(share: string): [string, string] {
  const percent = Number.parseInt(share, 10);
  return SHARE_COLUMNS[share] ?? [`${percent}fr`, `${100 - percent}fr`];
}
/** Ét stort og to små: the big card's share as the two columns, shared after the gap like the default 2fr 1fr. */
export function leadColumns(raw: unknown): string | undefined {
  const share = leadShareValue(raw);
  return share ? shareColumns(share).join(" ") : undefined;
}
function responsive(
  style: PurposeStyle,
  name: string,
  raw: unknown,
  convert: (v: string) => string | undefined,
) {
  if (typeof raw !== "string") return;
  const parts = raw.split("|").map((v) => v.trim());
  const mobile = convert(parts[0]);
  const desktop = convert(parts[1] ?? parts[0]);
  if (mobile !== undefined) style[`--pf-${name}`] = mobile;
  if (desktop !== undefined) style[`--pf-${name}-desktop`] = desktop;
}
/** Kontaktperson › Tekst ved siden af kortet: the text and card tracks from `textWidth` (2/3 while absent; the card never
 *  narrower than 18rem), `cardGap` per device and `pictureGap`. */
export function besideStyle(settings: PurposeSettings): PurposeStyle {
  const [text, card] = shareColumns(
    leadShareValue(settings.textWidth) ?? "2/3",
  );
  const style: PurposeStyle = {
    "--pf-text-track": `minmax(0, ${text})`,
    "--pf-card-track": `minmax(min(18rem, 100%), ${card})`,
  };
  responsive(style, "card-gap", settings.cardGap, lengthOrToken);
  const pictureGap = lengthOrToken(settings.pictureGap);
  if (pictureGap) style["--pf-picture-gap"] = pictureGap;
  return style;
}
export function purposeStyle(
  settings: PurposeSettings,
  buttonAppearance?: unknown,
  exact = false,
  /** How a stored text, border or rule colour in a styles area is written; the look system swaps an own colour's dark
   *  version in. */
  ink: (colour: string, area: string) => string = (colour) => colour,
  /** How a stored background colour is written, as `ink` is for text. */
  fill: (colour: string) => string = (colour) => colour,
): PurposeStyle {
  const style: PurposeStyle = {};
  responsive(
    style,
    "row-gap",
    settings.rowGap,
    (v) => lengthOrToken(v) ?? "24px",
  );
  responsive(
    style,
    "column-gap",
    settings.columnGap,
    (v) => lengthOrToken(v) ?? "24px",
  );
  if (typeof settings.columns === "string") {
    const [mobile, desktop = mobile] = settings.columns
      .split("|")
      .map((v) => v.trim());
    style["--pf-columns"] = COLUMN_COUNTS.includes(mobile) ? mobile : "1";
    style["--pf-columns-desktop"] = COLUMN_COUNTS.includes(desktop)
      ? desktop
      : "3";
  }
  responsive(style, "image-height", settings.imageHeight, (v) =>
    /%$/.test(v) ? undefined : lengthOrToken(v),
  );
  const imageWidth = imageWidthValue(settings.imageWidth);
  if (imageWidth)
    style["--pf-split-image"] = splitImage[imageWidth] ?? imageWidth;
  for (const [area, input] of Object.entries(settings.styles ?? {})) {
    if (!(PURPOSE_STYLE_AREAS as readonly string[]).includes(area)) continue;
    const s = Object.fromEntries(
      Object.entries(record(input)).filter(
        ([key]) => area !== "content" || !CONTENT_BOX_STYLES.includes(key),
      ),
    );
    if (FAMILY_BOXES.includes(area))
      responsive(style, `${area}-padding`, s.padding, lengthBox);
    else if (area === "itemCopy") {
      responsive(style, "itemCopy-padding", s.padding, lengthBox);
      responsive(style, "itemCopy-margin", s.margin, lengthBox);
    } else {
      if (area in LENGTH_PADDING)
        responsive(
          style,
          `${area}-padding`,
          s.padding,
          (v) => lengthBox(v) ?? LENGTH_PADDING[area],
        );
      else
        responsive(style, `${area}-padding`, s.padding, (v) => spacingCss(v));
      responsive(style, `${area}-margin`, s.margin, (v) => spacingCss(v));
    }
    responsive(style, `${area}-radius`, s.rounded, (v) =>
      getBorderRadius(v, "card", exact),
    );
    responsive(
      style,
      `${area}-size`,
      s.fontSize,
      (v) => sizes[v] || preciseTypographyValue(v, "font-size") || "inherit",
    );
    if (
      ["text", "itemText"].includes(area) &&
      typeof s.fontSize === "string" &&
      s.fontSize
        .split("|")
        .some((value) => preciseTypographyValue(value, "font-size"))
    )
      style[`--pf-${area}-exact`] = "1";
    if (typeof s.backgroundColor === "string")
      style[`--pf-${area}-background`] = fill(s.backgroundColor);
    if (
      typeof s.textColor === "string" &&
      !(area === "content" && ["auto", "light", "dark"].includes(s.textColor))
    )
      style[`--pf-${area}-color`] = ink(s.textColor, area);
    if (typeof s.borderColor === "string")
      style[`--pf-${area}-border-color`] = ink(s.borderColor, area);
    if (["none", "thin", "thick"].includes(string(s.borderWidth)))
      style[`--pf-${area}-border-width`] =
        s.borderWidth === "thin"
          ? "1px"
          : s.borderWidth === "thick"
            ? "2px"
            : "0px";
    if (["left", "center", "right"].includes(string(s.align))) {
      style[`--pf-${area}-align`] = string(s.align);
      style[`--pf-${area}-justify`] = {
        left: "flex-start",
        center: "center",
        right: "flex-end",
      }[string(s.align)];
    }
    if (["300", "400", "500", "600", "700"].includes(string(s.fontWeight)))
      style[`--pf-${area}-weight`] = string(s.fontWeight);
    if (["normal", "italic"].includes(string(s.fontStyle)))
      style[`--pf-${area}-style`] = string(s.fontStyle);
    if (["none", "uppercase"].includes(string(s.textTransform)))
      style[`--pf-${area}-transform`] = string(s.textTransform);
    const letterSpacing =
      tracking[string(s.letterSpacing)] ??
      preciseTypographyValue(s.letterSpacing, "letter-spacing");
    if (letterSpacing !== undefined)
      style[`--pf-${area}-tracking`] = letterSpacing;
    if (
      ["heading", "itemTitle"].includes(area) &&
      ["short", "full"].includes(string(s.rule))
    ) {
      style[`--pf-${area}-rule-width`] = s.rule === "short" ? "80px" : "100%";
      if (typeof s.ruleColor === "string")
        style[`--pf-${area}-rule-color`] = ink(s.ruleColor, area);
      const align =
        string(s.align) || string(record(settings.styles?.content).align);
      style[`--pf-${area}-rule-margin`] =
        align === "center" ? "auto" : align === "right" ? "auto 0" : "0 auto";
    }
    const lineHeight = [...LINE_HEIGHT_SCALE, ...EARLIER_LINE_HEIGHTS].includes(
      string(s.lineHeight),
    )
      ? string(s.lineHeight)
      : preciseTypographyValue(s.lineHeight, "line-height");
    if (lineHeight !== undefined)
      style[`--pf-${area}-line-height`] = lineHeight;
    if (area === "itemCopy" && lengthOrToken(s.width))
      style["--pf-itemCopy-width"] = lengthOrToken(s.width);
    if (s.width && string(s.width) in widths)
      style[`--pf-${area}-width`] = widths[string(s.width)];
    if (area === "copy" && string(s.blur) in GLASS)
      style["--pf-copy-blur"] = GLASS[string(s.blur)];
    if (string(s.shadow) in SHADOW)
      style[`--pf-${area}-shadow`] = SHADOW[string(s.shadow)];
  }
  const action = record(buttonAppearance);
  responsive(
    style,
    "action-size",
    action.fontSize,
    (value) => sizes[value] || preciseTypographyValue(value, "font-size"),
  );
  responsive(style, "action-padding", action.padding, lengthBox);
  responsive(style, "action-width", action.width, (value) =>
    value === "auto" ? "auto" : lengthOrToken(value),
  );
  const actionLine = preciseTypographyValue(action.lineHeight, "line-height");
  if (actionLine) style["--pf-action-line-height"] = actionLine;
  const actionTracking =
    tracking[string(action.letterSpacing)] ??
    preciseTypographyValue(action.letterSpacing, "letter-spacing");
  if (actionTracking !== undefined)
    style["--pf-action-tracking"] = actionTracking;
  if (["300", "400", "500", "600", "700"].includes(string(action.fontWeight)))
    style["--pf-action-weight"] = string(action.fontWeight);
  for (const name of ["row-gap", "column-gap", "columns", "image-height"]) {
    const key = `--pf-${name}` as const;
    if (style[key] !== undefined) {
      style[`--pf-${name}-mobile`] = style[key];
      delete style[key];
    }
  }
  return style;
}
export function eventHasEnded(
  settings: PurposeSettings,
  now = new Date(),
): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(settings.date || "")) return false;
  try {
    const today = new Intl.DateTimeFormat("sv-SE", {
      timeZone: settings.timeZone || "Europe/Copenhagen",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .format(now)
      .replace(" ", "T");
    return (
      `${settings.date}T${/^\d{2}:\d{2}$/.test(settings.endTime || "") ? settings.endTime : "23:59"}` <
      today
    );
  } catch {
    return false;
  }
}

export function eventDateLabel(date?: string, short = false): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) return "";
  const parsed = new Date(`${date}T12:00:00Z`);
  if (!Number.isFinite(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("da-DK", {
    timeZone: "UTC",
    ...(short
      ? { month: "short" }
      : { day: "numeric", month: "long", year: "numeric" }),
  }).format(parsed);
}
